import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { ArrowLeft, Sun, DollarSign, Zap, TrendingDown, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const SolarCalculatorPage = () => {
  const [monthlyBill, setMonthlyBill] = useState(150);
  const [roofSize, setRoofSize] = useState("medium");
  const [sunExposure, setSunExposure] = useState("full");
  const [showResults, setShowResults] = useState(false);

  const kwhRate = 0.13;
  const monthlyKwh = monthlyBill / kwhRate;
  const annualKwh = monthlyKwh * 12;
  const sunMultiplier = sunExposure === "full" ? 1 : sunExposure === "partial" ? 0.75 : 0.55;
  const peakSunHours = 6.5 * sunMultiplier;
  const systemSizeKw = annualKwh / (365 * peakSunHours * 0.8);
  const panelCount = Math.ceil(systemSizeKw / 0.4);
  const grossCost = Math.round(systemSizeKw * 1000 * 2.75);
  const nmCredit = Math.round(Math.min(grossCost * 0.1, 6000));
  const netCost = grossCost - nmCredit;
  const annualSavings = Math.round(monthlyBill * 12 * 0.85);
  const paybackYears = (netCost / annualSavings).toFixed(1);
  const twentyYearSavings = Math.round(annualSavings * 25 - netCost);

  return (
    <>
      <Helmet>
        <title>Solar Savings Calculator | Zenith Solutions</title>
        <meta
          name="description"
          content="Calculate your solar savings based on real NM solar data, current incentives, and local electricity rates."
        />
      </Helmet>
      <Navbar />
      <div className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto max-w-4xl px-4">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> All Tools
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Sun className="h-5 w-5 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-foreground">Solar Savings Calculator</h1>
            </div>
            <p className="text-muted-foreground mb-10 ml-[52px]">
              Estimated savings based on real NM solar data, current incentives, and local rates.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-foreground mb-3">
                  Monthly Electricity Bill: <span className="text-primary">${monthlyBill}</span>
                </label>
                <Slider
                  value={[monthlyBill]}
                  onValueChange={(v) => {
                    setMonthlyBill(v[0]);
                    setShowResults(false);
                  }}
                  min={50}
                  max={500}
                  step={10}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>$50</span>
                  <span>$500</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Usable Roof Size</label>
                <Select
                  value={roofSize}
                  onValueChange={(v) => {
                    setRoofSize(v);
                    setShowResults(false);
                  }}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (under 800 sq ft)</SelectItem>
                    <SelectItem value="medium">Medium (800–1,500 sq ft)</SelectItem>
                    <SelectItem value="large">Large (1,500+ sq ft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Sun Exposure</label>
                <Select
                  value={sunExposure}
                  onValueChange={(v) => {
                    setSunExposure(v);
                    setShowResults(false);
                  }}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Sun (no shade)</SelectItem>
                    <SelectItem value="partial">Partial Shade (some trees/structures)</SelectItem>
                    <SelectItem value="heavy">Heavy Shade (significant obstruction)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setShowResults(true)} size="lg" className="w-full h-14 text-base font-bold gap-2">
                <Zap className="h-5 w-5" /> Calculate My Savings
              </Button>
            </div>

            <div>
              {showResults ? (
                <div className="space-y-5 animate-fade-in-up">
                  <h3 className="text-xl font-black text-foreground">Your Estimated Savings</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: Sun, value: `${systemSizeKw.toFixed(1)} kW`, label: "System Size" },
                      { icon: Zap, value: `${panelCount}`, label: "Solar Panels" },
                      { icon: DollarSign, value: `$${annualSavings.toLocaleString()}`, label: "Annual Savings" },
                      { icon: TrendingDown, value: `${paybackYears} yrs`, label: "Payback Period" },
                    ].map((s) => (
                      <div key={s.label} className="bg-secondary rounded-xl p-4 text-center">
                        <s.icon className="h-5 w-5 text-primary mx-auto mb-1.5" />
                        <p className="text-xl font-black text-foreground">{s.value}</p>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 space-y-2 text-sm">
                    <h4 className="font-bold text-foreground">Cost Breakdown</h4>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Gross System Cost</span>
                      <span className="font-semibold text-foreground">${grossCost.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">NM State Credit (up to $6,000)</span>
                      <span className="font-semibold text-primary">-${nmCredit.toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold text-foreground">Net Cost</span>
                      <span className="font-black text-foreground text-lg">${netCost.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="bg-primary text-primary-foreground rounded-xl p-5 text-center">
                    <p className="text-sm font-medium text-primary-foreground/70">25-Year Net Savings</p>
                    <p className="text-3xl font-black">${twentyYearSavings.toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    *Estimates based on NM average solar irradiance (6.5 peak sun hours), current NM electricity rates
                    ($0.13/kWh), and NM state solar credit. Actual savings may vary.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="tel:5056048536" className="flex-1">
                      <Button size="lg" className="w-full h-12 font-bold gap-2">
                        <Phone className="h-4 w-4" /> Call for Custom Quote
                      </Button>
                    </a>
                    <Link to="/get-estimate" className="flex-1">
                      <Button size="lg" variant="outline" className="w-full h-12 font-bold">
                        Request Free Estimate
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-muted-foreground">
                    <Sun className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">Adjust your details and click calculate</p>
                    <p className="text-sm mt-1">to see your estimated solar savings</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </>
  );
};

export default SolarCalculatorPage;
